/**
 * `kind: 'choice'`: pick from options the ask carried. The range comes from core's
 * `choiceRange` so it can't disagree with the server. Every tap submits at once — a single
 * choice sends the one id; a multiple toggles and resubmits the whole set as it changes.
 */

import { useState } from 'react';
import type { InputSpec } from '@gsf/contract';
import { choiceRange } from '@gsf/core';
import type { RendererProps } from '../renderer';

/** The ids this viewer has already sent, out of the echo the ask offered. */
function mineFrom(value: unknown): readonly string[] {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.filter((one): one is string => typeof one === 'string');
  return [];
}

export function ChoiceInput({
  request,
  spec,
  submit,
  disabled,
  size,
}: RendererProps<Extract<InputSpec, { kind: 'choice' }>>) {
  const [min, max] = choiceRange(spec);
  const single = min === 1 && max === 1;
  const already = mineFrom(request.mine?.value);
  // Multi keeps a local set so each tap lights up instantly; seeded from any answer this
  // viewer already has (a rejoin) and then driving the submits.
  const [staged, setStaged] = useState<readonly string[]>(already);
  const chosen = new Set(single ? already : staged);

  function toggleMulti(id: string) {
    const next = chosen.has(id)
      ? staged.filter((one) => one !== id)
      : staged.length < max
        ? [...staged, id]
        : staged;
    setStaged(next);
    // Submit every change that meets the ask's minimum; an empty set is below it and
    // can't be sent, so the last valid pick stands until they choose again.
    if (next.length >= min) submit([...next]);
  }

  return (
    <>
      {request.prompt ? <p className="play-clue">{request.prompt}</p> : null}

      {spec.options.length === 0 ? (
        // Reachable: a hand can be empty, or a round can end while a phone looks at it.
        <p className="faint">Nothing to pick right now.</p>
      ) : (
        <div className="col choice-options">
          {spec.options.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`btn choice-option${chosen.has(option.id) ? ' btn-primary' : ''}${
                size === 'lg' ? ' btn-lg' : ''
              }`}
              disabled={disabled}
              onClick={() => (single ? submit(option.id) : toggleMulti(option.id))}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {chosen.size > 0 && request.allowRevision ? (
        <p className="faint">
          {single ? 'Locked in — tap another to change it.' : 'Saved as you go — tap to change your picks.'}
        </p>
      ) : null}
    </>
  );
}
